import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RichEditor } from "@/components/RichEditor";
import { toast } from "sonner";
import { Send, Wand2, Mail as MailIcon, ArrowLeft, Building2 } from "lucide-react";

export default function Review() {
  const nav = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [subject, setSubject] = useState("Interested in opportunities at your company");
  const [coverMode, setCoverMode] = useState("generic");
  const [genericHtml, setGenericHtml] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [smtpOk, setSmtpOk] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("aiapply_selected");
    const ids = raw ? JSON.parse(raw) : [];
    setSelectedIds(ids);
    (async () => {
      try {
        const [cRes, sRes] = await Promise.all([api.get("/companies"), api.get("/smtp/config")]);
        setCompanies((cRes.data.companies || []).filter((c) => ids.includes(c.id)));
        setSmtpOk(Boolean(sRes.data.config));
      } catch (e) { if (e.response?.status === 401) nav("/login"); }
    })();
  }, [nav]);

  const textFromHtml = (html) => {
    const d = document.createElement("div");
    d.innerHTML = html || "";
    return (d.textContent || "").trim();
  };

  const generateGeneric = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/cover-letter/generate", { mode: "generic", tone: "professional" });
      const paragraphs = (data.letter || "").split(/\n\n+/).filter(Boolean);
      const html = paragraphs.map((p) => `<p>${p.replace(/\n/g, "<br>").replace(/</g, "&lt;")}</p>`).join("");
      setGenericHtml(html);
      toast.success("Cover letter drafted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const send = async () => {
    if (selectedIds.length === 0) return toast.error("No companies selected");
    if (!smtpOk) return toast.error("Connect your email inbox first");
    if (coverMode === "generic" && !textFromHtml(genericHtml)) return toast.error("Write or generate a cover letter first");
    setSending(true);
    try {
      const { data } = await api.post("/applications/send", {
        company_ids: selectedIds, subject,
        cover_letter_mode: coverMode,
        generic_cover_letter: coverMode === "generic" ? textFromHtml(genericHtml) : null,
        generic_cover_letter_html: coverMode === "generic" ? genericHtml : null,
      });
      const ok = data.results.filter((r) => r.status === "sent").length;
      const fail = data.results.length - ok;
      toast.success(`Sent ${ok} emails${fail ? `, ${fail} failed` : ""}. See History.`);
      sessionStorage.removeItem("aiapply_selected");
      nav("/app/history");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const totalEmails = companies.reduce((n, c) => n + (c.emails?.length || 0), 0);

  return (
    <div className="flex min-h-screen bg-[#FDFDFB]">
      <Sidebar />
      <main className="flex-1 max-w-7xl mx-auto px-6 md:px-10 py-10 w-full">
        <div className="mb-8">
          <Link to="/app" className="text-xs text-[#5C6661] hover:text-[#121413] inline-flex items-center gap-1" data-testid="back-discover"><ArrowLeft className="w-3 h-3" /> Back to discover</Link>
          <div className="overline mt-4">Review & send</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">One final look before it flies.</h1>
        </div>

        {!smtpOk && (
          <Card className="mb-6 p-4 border-[#E2E8E4] bg-[#FBF3DE] flex items-center justify-between" data-testid="smtp-warning">
            <div className="flex items-center gap-3"><MailIcon className="w-4 h-4 text-[#B7791F]" />
              <div className="text-sm text-[#5C4A00]">Connect your email inbox before sending applications.</div>
            </div>
            <Button onClick={() => nav("/app/smtp")} size="sm" variant="outline" className="rounded-full border-[#E2C078]" data-testid="setup-smtp-btn">Set up email</Button>
          </Card>
        )}

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <Card className="p-6 border-[#E2E8E4]">
              <Tabs value={coverMode} onValueChange={setCoverMode} data-testid="cover-tabs">
                <TabsList className="bg-[#F2F4F3]">
                  <TabsTrigger value="generic" data-testid="tab-generic">Shared cover letter</TabsTrigger>
                  <TabsTrigger value="personalized" data-testid="tab-personalized">AI-personalize per company</TabsTrigger>
                </TabsList>
                <TabsContent value="generic" className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm">Your message</Label>
                    <Button onClick={generateGeneric} disabled={generating} variant="outline" size="sm" className="rounded-full border-[#E2E8E4]" data-testid="generate-generic-btn"><Wand2 className="w-3.5 h-3.5 mr-2" /> {generating ? "Drafting…" : "Draft with AI"}</Button>
                  </div>
                  <RichEditor value={genericHtml} onChange={setGenericHtml}
                              placeholder="Dear Hiring Team, I'm writing to express my interest in joining your team…"
                              data-testid="generic-letter" />
                  <div className="mt-2 text-[11px] text-[#5C6661]">Use <span className="font-semibold">Bold</span>, <em>italics</em>, and paragraphs — companies see it formatted, with a plain-text fallback.</div>
                </TabsContent>
                <TabsContent value="personalized" className="mt-4">
                  <div className="rounded-lg bg-[#E7F0EB]/60 border border-[#D8E5DC] p-4 text-sm text-[#2D4A3E]">
                    A unique cover letter will be generated for each selected company using your CV. This takes a few seconds per company.
                  </div>
                </TabsContent>
              </Tabs>
              <div className="mt-6">
                <Label className="overline">Email subject</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="email-subject" />
              </div>
            </Card>
          </div>
          <div className="space-y-4">
            <Card className="p-6 border-[#E2E8E4]">
              <div className="overline">Summary</div>
              <div className="mt-3 space-y-1 text-sm">
                <div>Selected: <span className="font-semibold" data-testid="summary-selected">{selectedIds.length}</span></div>
                <div>Emails to send: <span className="font-semibold" data-testid="summary-emails">{totalEmails}</span></div>
                <div className="text-xs text-[#5C6661] flex items-center gap-1 mt-2">{smtpOk ? <><MailIcon className="w-3 h-3 text-[#2D4A3E]" /> Inbox connected</> : <>Inbox not connected</>}</div>
              </div>
            </Card>
            <Card className="p-4 border-[#E2E8E4]">
              <div className="overline mb-2">Companies</div>
              <div className="space-y-1.5 max-h-60 overflow-y-auto">
                {companies.map((c) => (
                  <div key={c.id} className="flex items-center gap-2 text-sm py-1" data-testid={`review-company-${c.id}`}>
                    <Building2 className="w-3.5 h-3.5 text-[#5C6661]" />
                    <span className="truncate">{c.name}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Button onClick={send} disabled={sending} className="w-full rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50] h-11" data-testid="send-applications-btn">
              <Send className="w-4 h-4 mr-2" />{sending ? "Sending from your inbox…" : `Send to ${selectedIds.length} companies`}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
